import {
  authForgot,
  authLogin,
  authLogout,
  authReset,
  authVerifyToken,
  generalSetting,
} from '@/store/actions';
import { apiService } from './apiService';
import { setBearerToken, setBranchId, setCompanyId } from '@/security/Axios';
import ToastMe from '@/components/ui/ToastMe';
import { setCredit, clearCredit } from '@/store/creditSlice';

/* Debug helpers */
const DEBUG_DEFAULT = true;
function isDebugOn() {
  try { return (typeof window !== 'undefined' && !!window.__DEBUG__) || DEBUG_DEFAULT; } catch { return DEBUG_DEFAULT; }
}
function dbg(...a) { if (isDebugOn()) console.log('[AuthServices]', ...a); }

/* Env + storage helpers (bundler-agnostic) */
function getDefaultCompanyIdFromEnv() {
  let fromVite;
  try { fromVite = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_DEFAULT_COMPANY_ID) || undefined; } catch { fromVite = undefined; }
  const fromCRA  = (typeof process !== 'undefined' && process?.env?.REACT_APP_DEFAULT_COMPANY_ID) || undefined;
  const fromNext = (typeof process !== 'undefined' && process?.env?.NEXT_PUBLIC_DEFAULT_COMPANY_ID) || undefined;
  const fromPlain= (typeof process !== 'undefined' && process?.env?.DEFAULT_COMPANY_ID) || undefined;
  const fromWin  = (typeof window !== 'undefined' && window.__DEFAULT_COMPANY_ID__) || undefined;
  return fromVite || fromCRA || fromNext || fromPlain || fromWin || '';
}
function safeGetLS(key){ try{ return localStorage.getItem(key);}catch{return null;} }
function isObjectId(v){ return typeof v==='string' && /^[0-9a-fA-F]{24}$/.test(v); }
function decodeJwtPayload(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return {};
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}
function resolveCompanyId(data, options = {}){
  const { includeStorage = true } = options;
  const fromPayload = data?.company_id;
  const fromStorage = includeStorage ? safeGetLS('company_id') : null;
  const fromEnv     = getDefaultCompanyIdFromEnv();
  const id = (fromPayload || fromStorage || fromEnv || '').trim();
  if (isDebugOn()){
    console.groupCollapsed('[AuthServices] resolveCompanyId');
    console.log('payload:', fromPayload);
    console.log('localStorage:', fromStorage);
    console.log('env default:', fromEnv);
    console.log('chosen:', id, 'valid?', isObjectId(id));
    console.groupEnd();
  }
  return isObjectId(id) ? id : null;
}

/* ───────── Auth services ───────── */

export function loginAuthService(data) {
  const companyId = resolveCompanyId(data, { includeStorage: false });
  const body = companyId ? { ...data, company_id: companyId } : { ...data };
  setCompanyId(companyId || null);
  setBranchId(null);

  if (isDebugOn()) {
    const preview = { ...body }; if (preview.password) preview.password = '••••••';
    dbg('loginAuthService call → /auth/login', preview);
  }

  return apiService({
    method: 'POST',
    url: '/auth/login',
    data: body,
    onSuccess: (dispatch, res) => {
      const user       = res?.data?.data || {};
      const topToken   = res?.data?.token;
      const dataToken  = user?.token;
      const nestedToken= res?.data?.data?.data?.token;
      const token = topToken || dataToken || nestedToken;

      if (typeof token !== 'string' || token.split('.').length !== 3) {
        console.error('[AuthServices] Invalid token from /auth/login:', token);
        ToastMe('No valid token received from server.', 'error');
        return;
      }

      const tokenPayload = decodeJwtPayload(token);
      const resolvedCompanyId =
        user?.company_id ||
        user?.companyId ||
        tokenPayload?.company_id ||
        null;
      const resolvedBranchId =
        user?.default_branch_id ||
        user?.branch_id ||
        tokenPayload?.branch_id ||
        null;

      try { localStorage.setItem('jwt_token', token); } catch {}
      setBearerToken(token);
      if (resolvedCompanyId && isObjectId(String(resolvedCompanyId))) {
        setCompanyId(String(resolvedCompanyId));
      }
      if (resolvedBranchId && isObjectId(String(resolvedBranchId))) {
        setBranchId(String(resolvedBranchId));
      } else {
        setBranchId(null);
      }

      dispatch(authLogin({ data: { token, data: user }, message: res?.data?.message, success: res?.data?.success }));
      ToastMe(res?.data?.message || 'Login successful', 'success');
    },
  });
}
export function customerLoginAuthService(data) {
  const companyId = resolveCompanyId(data) || '64b17c2f1e34a5d9bdf10c01';
  const headers = { 'X-Company-Id': companyId };
  const body = { ...data, company_id: companyId };

  return apiService({
    method: 'POST',
    url: '/customer/customer-login',
    data: body,
    headers,
    transformRequest: [(payload, hdrs) => { try { if (hdrs && 'Authorization' in hdrs) delete hdrs.Authorization; } catch {} return JSON.stringify(payload); }],
    onSuccess: (dispatch, res) => {
      const customer = res.data?.data?.customer || res.data?.data || {};
      const token = res?.data?.data?.token;

      if (typeof token !== 'string' || token.split('.').length !== 3) {
        try { localStorage.removeItem('customer_jwt'); localStorage.removeItem('customerToken'); } catch {}
        ToastMe('No valid token received', 'error'); return;
      }

      try { localStorage.setItem('customer_jwt', token); localStorage.setItem('customerToken', token); } catch {}
      try { setCompanyId(companyId); } catch {}
      setBearerToken(token);

      dispatch(authLogin({ data: { token, data: customer }, message: res.data.message, success: res.data.success }));
      dispatch(setCredit({
        enabled: !!customer.creditEnabled,
        balance: (Number(customer.creditLimit) || 0) - (Number(customer.outstandingCredit) || 0),
        limit: Number(customer.creditLimit) || 0,
      }));
      ToastMe(res.data.message, 'success');
    },
    onFailure: () => { ToastMe('Customer login failed. Please check your credentials.', 'error'); },
  });
}

export function logoutAuthService() {
  return async (dispatch) => {
    dispatch(authLogout());
    dispatch(clearCredit());
    try { localStorage.removeItem('jwt_token'); localStorage.removeItem('customer_jwt'); localStorage.removeItem('customerToken'); } catch {}
    setBearerToken(null);
    ToastMe('Logged out successfully.', 'success');
  };
}

/* ─────────────────────────────────────────────
   PUBLIC: forgot / verify / reset
   - send X-Company-Id header AND company_id in body
   - strip Authorization header
───────────────────────────────────────────── */

function publicHeadersAndBody(data) {
  const companyId = resolveCompanyId(data) || '64b17c2f1e34a5d9bdf10c01'; // safe fallback in dev
  return {
    headers: { 'X-Company-Id': companyId },
    body: { ...data, company_id: companyId },
  };
}
const stripAuthTransform = [(payload, headers) => {
  try {
    if (headers) {
      delete headers.Authorization;
      if (headers.common) delete headers.common.Authorization;
    }
  } catch {}
  return JSON.stringify(payload);
}];

export function forgetPasswordAuthService(data) {
  const { headers, body } = publicHeadersAndBody(data);
  return apiService({
    method: 'POST',
    url: '/auth/forgot-password',
    data: body,
    headers,
    transformRequest: stripAuthTransform,
    onSuccess: (dispatch, res) => {
      dispatch(authForgot(res));
      ToastMe(res.data.message, 'success');
    },
  });
}

export function verifyTokenAuthService(data) {
  const { headers, body } = publicHeadersAndBody(data);
  return apiService({
    method: 'POST',
    url: '/auth/verify-token',
    data: body,
    headers,
    transformRequest: stripAuthTransform,
    onSuccess: (dispatch, res) => {
      dispatch(authVerifyToken(res));
      ToastMe(res.data.message, 'success');
    },
  });
}

export function resetPasswordAuthService(data) {
  const { headers, body } = publicHeadersAndBody(data);
  return apiService({
    method: 'POST',
    url: '/auth/update-password',
    data: body,
    headers,
    transformRequest: stripAuthTransform,
    onSuccess: (dispatch, res) => {
      dispatch(authReset(res));
      ToastMe(res.data.message, 'success');
    },
  });
}

export function loginVerifyCheckAuthService() {
  return apiService({
    method: 'GET',
    url: '/site/check-verify',
    onFailure: (dispatch) => {
      dispatch(authLogout());
      dispatch(clearCredit());
    },
  });
}

export function getGeneralSettingStoreAfterLoginService(data) {
  return apiService({
    method: 'POST',
    url: '/front/setting/data',
    data,
    headers: { Authorization: undefined },
    transformRequest: [(payload, headers) => {
      try {
        if (headers) {
          delete headers.Authorization;
          if (headers.common) delete headers.common.Authorization;
        }
      } catch {}
      return JSON.stringify(payload);
    }],
    onSuccess: (dispatch, res) => {
      dispatch(generalSetting(res.data));
    },
  });
}
